import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Settings, AlertTriangle, DollarSign, Globe, Flag, LayoutGrid, Plus, Trash2, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { logAdminAction } from '@/lib/auditLog';
import { ListingPicker, type PickedListing, type ListingType } from './internal/ListingPicker';
import { generalSchema, currencyRateSchema, maintenanceSchema } from '@/lib/systemSettingsValidation';

const SLOTS = ['hero', 'featured_carousel', 'trending'] as const;
type Slot = typeof SLOTS[number];

const CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];

export function SystemSettings() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['system-settings-admin'],
    queryFn: async () => {
      const { data } = await supabase.from('system_settings' as any).select('*');
      const map: Record<string, any> = {};
      (data || []).forEach((row: any) => { map[row.key] = row.value; });
      return map;
    },
  });

  const { data: rates } = useQuery({
    queryKey: ['currency-rates-history'],
    queryFn: async () => {
      const { data } = await supabase.from('currency_rates' as any).select('*').order('fetched_at', { ascending: true }).limit(30);
      return data || [];
    },
  });

  const { data: curation } = useQuery({
    queryKey: ['homepage-curation-admin'],
    queryFn: async () => {
      const { data } = await supabase.from('homepage_curation' as any).select('*').order('slot').order('position');
      return data || [];
    },
  });

  // Local edit state
  const [defaultCity, setDefaultCity] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('ETB');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [etbToUsd, setEtbToUsd] = useState('');
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [pendingMaintenance, setPendingMaintenance] = useState(false);
  const [flags, setFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!settings) return;
    setDefaultCity(settings.default_city?.value || 'Addis Ababa');
    setDefaultCurrency(settings.default_currency?.value || 'ETB');
    setContactEmail(settings.site_contact?.email || '');
    setContactPhone(settings.site_contact?.phone || '');
    setEtbToUsd(String(settings.etb_to_usd_rate?.value ?? 0.0078));
    setMaintenanceEnabled(!!settings.maintenance_mode?.enabled);
    setMaintenanceMessage(settings.maintenance_mode?.message || '');
    setFlags(settings.feature_flags || {});
  }, [settings]);

  const upsertSetting = async (key: string, value: any, isPublic = true) => {
    const { error } = await supabase.from('system_settings' as any).upsert({
      key, value, is_public: isPublic, updated_by: user?.id, updated_at: new Date().toISOString(),
    });
    if (error) throw error;
    await logAdminAction({ action: 'update_setting', entity_type: 'system_setting', metadata: { key, value } });
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['system-settings-admin'] });
    qc.invalidateQueries({ queryKey: ['system-settings'] });
  };

  const generalErrors = useMemo(() => {
    const r = generalSchema.safeParse({
      default_city: defaultCity, default_currency: defaultCurrency,
      contact_email: contactEmail, contact_phone: contactPhone,
    });
    if (r.success) return {} as Record<string, string>;
    const out: Record<string, string> = {};
    r.error.issues.forEach(i => { out[String(i.path[0])] = i.message; });
    return out;
  }, [defaultCity, defaultCurrency, contactEmail, contactPhone]);

  const rateErrors = useMemo(() => {
    const n = parseFloat(etbToUsd);
    const r = currencyRateSchema.safeParse({ rate: Number.isNaN(n) ? undefined : n });
    return r.success ? '' : r.error.issues[0]?.message || 'Invalid rate';
  }, [etbToUsd]);

  const maintenanceErrors = useMemo(() => {
    const r = maintenanceSchema.safeParse({ enabled: maintenanceEnabled, message: maintenanceMessage });
    return r.success ? '' : r.error.issues[0]?.message || '';
  }, [maintenanceEnabled, maintenanceMessage]);

  const saveGeneral = async () => {
    if (Object.keys(generalErrors).length) { toast.error('Fix the errors before saving'); return; }
    try {
      await Promise.all([
        upsertSetting('default_city', { value: defaultCity }),
        upsertSetting('default_currency', { value: defaultCurrency }),
        upsertSetting('site_contact', { email: contactEmail, phone: contactPhone }),
      ]);
      toast.success('General settings saved'); refresh();
    } catch (e: any) { toast.error(e.message); }
  };

  const saveCurrency = async () => {
    if (rateErrors) { toast.error(rateErrors); return; }
    const r = parseFloat(etbToUsd);
    try {
      await upsertSetting('etb_to_usd_rate', { value: r });
      await supabase.from('currency_rates' as any).insert({ base: 'ETB', quote: 'USD', rate: r, source: 'manual' });
      toast.success('Rate updated');
      qc.invalidateQueries({ queryKey: ['currency-rates-history'] });
      refresh();
    } catch (e: any) { toast.error(e.message); }
  };

  const confirmMaintenance = async () => {
    if (maintenanceErrors) { toast.error(maintenanceErrors); return; }
    try {
      await upsertSetting('maintenance_mode', { enabled: pendingMaintenance, message: maintenanceMessage });
      setMaintenanceEnabled(pendingMaintenance);
      toast.success(pendingMaintenance ? 'Maintenance mode ON' : 'Maintenance mode OFF');
      refresh();
    } catch (e: any) { toast.error(e.message); }
  };

  const saveFlags = async () => {
    try {
      await upsertSetting('feature_flags', flags);
      toast.success('Feature flags saved'); refresh();
    } catch (e: any) { toast.error(e.message); }
  };

  // Curation
  const [curationDialogOpen, setCurationDialogOpen] = useState(false);
  const [curationSlot, setCurationSlot] = useState<Slot>('hero');
  const [curationPicked, setCurationPicked] = useState<PickedListing | null>(null);
  const [curationType, setCurationType] = useState<ListingType>('property');
  const [curationStarts, setCurationStarts] = useState('');
  const [curationEnds, setCurationEnds] = useState('');

  const addCuration = async () => {
    if (!curationPicked) { toast.error('Pick a listing'); return; }
    const slotItems = (curation || []).filter((c: any) => c.slot === curationSlot);
    const nextPos = (slotItems.reduce((m: number, c: any) => Math.max(m, c.position), 0) || 0) + 1;
    const storedType = curationPicked.type === 'commercial' ? 'property' : curationPicked.type;
    const { data, error } = await supabase.from('homepage_curation' as any).insert({
      slot: curationSlot,
      listing_type: storedType,
      listing_id: curationPicked.id,
      position: nextPos,
      is_active: true,
      starts_at: curationStarts || new Date().toISOString(),
      ends_at: curationEnds || null,
      created_by: user?.id,
    }).select('id').single();
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'add_curation', entity_type: 'homepage_curation', entity_id: (data as any)?.id, metadata: { slot: curationSlot, listing_type: storedType, listing_id: curationPicked.id } });
    toast.success('Added to slot');
    setCurationDialogOpen(false); setCurationPicked(null); setCurationStarts(''); setCurationEnds('');
    qc.invalidateQueries({ queryKey: ['homepage-curation-admin'] });
  };

  const removeCuration = async (id: string) => {
    const { error } = await supabase.from('homepage_curation' as any).delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'remove_curation', entity_type: 'homepage_curation', entity_id: id });
    qc.invalidateQueries({ queryKey: ['homepage-curation-admin'] });
  };

  const moveCuration = async (row: any, dir: -1 | 1) => {
    const newPos = Math.max(1, row.position + dir);
    await supabase.from('homepage_curation' as any).update({ position: newPos }).eq('id', row.id);
    qc.invalidateQueries({ queryKey: ['homepage-curation-admin'] });
  };

  const toggleCurationActive = async (row: any) => {
    await supabase.from('homepage_curation' as any).update({ is_active: !row.is_active }).eq('id', row.id);
    qc.invalidateQueries({ queryKey: ['homepage-curation-admin'] });
  };

  const rateChartData = useMemo(() => (rates || []).map((r: any) => ({
    date: format(parseISO(r.fetched_at), 'MMM d'),
    rate: Number(r.rate),
  })), [rates]);

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Settings className="h-6 w-6 text-primary" /> System Settings</h1>
        <p className="text-sm text-muted-foreground">Global platform configuration.</p>
      </div>

      {/* General */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4" /> General</CardTitle>
          <CardDescription>Defaults used across the public site.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Default city</Label>
              <Select value={defaultCity} onValueChange={setDefaultCity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default currency</Label>
              <Select value={defaultCurrency} onValueChange={setDefaultCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ETB">ETB (Birr)</SelectItem>
                  <SelectItem value="USD">USD (Dollar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact email</Label>
              <Input value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
              {generalErrors.contact_email && <p className="text-xs text-destructive mt-1">{generalErrors.contact_email}</p>}
            </div>
            <div>
              <Label>Contact phone</Label>
              <Input value={contactPhone} onChange={e => setContactPhone(e.target.value)} />
              {generalErrors.contact_phone && <p className="text-xs text-destructive mt-1">{generalErrors.contact_phone}</p>}
            </div>
          </div>
          <Button onClick={saveGeneral} disabled={Object.keys(generalErrors).length > 0}>Save general</Button>
        </CardContent>
      </Card>

      {/* Currency */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" /> Currency</CardTitle>
          <CardDescription>Manual ETB ↔ USD conversion rate.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1 max-w-[260px]">
              <Label>1 ETB = ? USD</Label>
              <Input type="number" step="0.0001" value={etbToUsd} onChange={e => setEtbToUsd(e.target.value)} />
              {rateErrors && <p className="text-xs text-destructive mt-1">{rateErrors}</p>}
            </div>
            <Button onClick={saveCurrency} disabled={!!rateErrors}>Update rate</Button>
          </div>
          {!rateErrors && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span>Live preview: <strong>ETB 100,000</strong> = <strong>USD {(100000 * parseFloat(etbToUsd || '0')).toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></span>
            </div>
          )}
          {rateChartData.length > 1 && (
            <div style={{ height: 160 }}>
              <ResponsiveContainer>
                <LineChart data={rateChartData}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                  <Tooltip />
                  <Line type="monotone" dataKey="rate" stroke="hsl(var(--primary))" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Maintenance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Maintenance Mode</CardTitle>
          <CardDescription>Show a banner across the public site when enabled.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={maintenanceEnabled}
              onCheckedChange={(v) => { setPendingMaintenance(v); }}
            />
            <span className="text-sm">{maintenanceEnabled ? 'ON' : 'OFF'}</span>
            {maintenanceEnabled && <Badge variant="destructive">Live banner</Badge>}
          </div>
          <div>
            <Label>Banner message <span className="text-xs text-muted-foreground">({maintenanceMessage.length}/280)</span></Label>
            <Textarea value={maintenanceMessage} onChange={e => setMaintenanceMessage(e.target.value)} placeholder="We're performing scheduled maintenance…" />
            {maintenanceErrors && <p className="text-xs text-destructive mt-1">{maintenanceErrors}</p>}
          </div>
          {maintenanceMessage && !maintenanceErrors && (
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Eye className="h-3 w-3" /> Live preview</Label>
              <Alert variant="destructive" className="mt-1">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{maintenanceMessage}</AlertDescription>
              </Alert>
            </div>
          )}
          <Button
            variant="outline"
            disabled={!!maintenanceErrors}
            onClick={() => upsertSetting('maintenance_mode', { enabled: maintenanceEnabled, message: maintenanceMessage })
              .then(() => { toast.success('Saved'); refresh(); })
              .catch(e => toast.error(e.message))}
          >
            Save message only
          </Button>
        </CardContent>
      </Card>

      {/* Confirm maintenance toggle */}
      <Dialog open={pendingMaintenance !== maintenanceEnabled} onOpenChange={(o) => !o && setPendingMaintenance(maintenanceEnabled)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pendingMaintenance ? 'Enable' : 'Disable'} maintenance mode?</DialogTitle>
            <DialogDescription>
              {pendingMaintenance
                ? 'The public site will display the maintenance banner immediately.'
                : 'The maintenance banner will be removed from the public site immediately.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingMaintenance(maintenanceEnabled)}>Cancel</Button>
            <Button variant={pendingMaintenance ? 'destructive' : 'default'} onClick={confirmMaintenance}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feature flags */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Flag className="h-4 w-4" /> Feature Flags</CardTitle>
          <CardDescription>Hide unfinished surfaces or roll out gradually.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {['email_channel', 'payments_live', 'boost_console', 'reviews_moderation'].map(flag => (
            <div key={flag} className="flex items-center justify-between border rounded-md p-3">
              <span className="text-sm font-medium capitalize">{flag.replace(/_/g, ' ')}</span>
              <Switch checked={!!flags[flag]} onCheckedChange={(v) => setFlags(prev => ({ ...prev, [flag]: v }))} />
            </div>
          ))}
          <Button onClick={saveFlags}>Save flags</Button>
        </CardContent>
      </Card>

      {/* Homepage curation */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> Homepage Curation</CardTitle>
            <CardDescription>Curate Hero, Featured Carousel, and Trending slots.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setCurationDialogOpen(true)}><Plus className="h-4 w-4 mr-1" /> Add listing</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {SLOTS.map(slot => {
            const items = (curation || []).filter((c: any) => c.slot === slot).sort((a: any, b: any) => a.position - b.position);
            return (
              <div key={slot}>
                <h3 className="text-sm font-semibold capitalize mb-2">{slot.replace(/_/g, ' ')} <span className="text-xs text-muted-foreground font-normal">({items.length})</span></h3>
                {items.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No listings curated for this slot.</p>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[60px]">Pos</TableHead>
                          <TableHead>Listing</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Active</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((c: any) => (
                          <TableRow key={c.id}>
                            <TableCell className="font-mono">{c.position}</TableCell>
                            <TableCell className="text-xs font-mono">{c.listing_id.slice(0, 8)}…</TableCell>
                            <TableCell className="capitalize text-sm">{c.listing_type}</TableCell>
                            <TableCell><Switch checked={c.is_active} onCheckedChange={() => toggleCurationActive(c)} /></TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" onClick={() => moveCuration(c, -1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => moveCuration(c, 1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => removeCuration(c.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Add curation dialog */}
      <Dialog open={curationDialogOpen} onOpenChange={setCurationDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Curate a listing</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Slot</Label>
              <Select value={curationSlot} onValueChange={(v) => setCurationSlot(v as Slot)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SLOTS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ListingPicker value={curationPicked} onChange={setCurationPicked} initialType={curationType} onTypeChange={setCurationType} />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Starts at</Label><Input type="datetime-local" value={curationStarts} onChange={e => setCurationStarts(e.target.value)} /></div>
              <div><Label>Ends at (optional)</Label><Input type="datetime-local" value={curationEnds} onChange={e => setCurationEnds(e.target.value)} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCurationDialogOpen(false)}>Cancel</Button>
            <Button onClick={addCuration} disabled={!curationPicked}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
